import { Session } from 'src/models/session/_types';
import { Trainer } from 'src/models/trainer/_types';
import { User } from 'src/models/user/_types';

import { MeasurementFieldConfig } from './measurement';

interface getSortedMeasurementFieldsForUserOptions {
  user: User;
  session: Session;
  trainer?: Trainer;
}

/**
 * The actual fields that a user should see are dependent on a lot of things:
 *
 * `session.statsList`
 * The initial list of fields for the whole platform. Fields with `hidden` are hidden for all users
 *
 * `user.hiddenMeasurements`
 * An array of keys for items in session.statsList that should be hidden for that user
 *
 * `trainer.measurementsOrder`
 * An array of keys for items in session.statsList in the order they should be displayed
 *
 * This combines all of them into a sorted `MeasurementFieldConfig[]` with globally hidden
 * items removed entirely and fields hidden just for the user as `hidden`
 */
export const getSortedMeasurementFieldsForUser = ({
  user,
  trainer,
  session,
}: getSortedMeasurementFieldsForUserOptions): MeasurementFieldConfig[] => {
  const initialStatsList = session.statsList;
  const userLevelHiddenFields = user.hiddenMeasurements || [];
  const trainerLevelFieldOrder = trainer?.measurementsOrder;

  const fieldsWithGloballyHiddenRemoved = initialStatsList.filter(
    (field) => !field.hidden
  );
  const fieldsWithUserLevelHidden = fieldsWithGloballyHiddenRemoved.map(
    (field) => {
      const shouldBeHidden = !!userLevelHiddenFields.find(
        (hiddenFieldLabel) => field.label === hiddenFieldLabel
      );
      return {
        ...field,
        hidden: shouldBeHidden,
      };
    }
  );

  if (trainerLevelFieldOrder && trainerLevelFieldOrder.length > 0) {
    // First, get fields in the trainer's specified order
    const orderedFields = trainerLevelFieldOrder
      .map((fieldLabel) => {
        return fieldsWithUserLevelHidden.find(
          (field) => field.label === fieldLabel
        );
      })
      .filter((field): field is MeasurementFieldConfig => !!field);

    // Then, get any remaining fields that aren't in the trainer's order
    const remainingFields = fieldsWithUserLevelHidden.filter(
      (field) => !trainerLevelFieldOrder.includes(field.label)
    );

    // Return ordered fields first, followed by remaining fields
    return [...orderedFields, ...remainingFields];
  }

  return fieldsWithUserLevelHidden;
};
